import { cloneDeep, differenceWith, find, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  CreateFinancialSituationAction,
  DeleteFinancialSituationAction,
  FetchFinancialSituationsAction,
  FinancialSituation,
  ReduxStoreData,
  ServerFinancialSituationCreate,
  ServerFinancialSituationUpdate,
  UpdateFinancialSituationAction,
  UpsertFinancialSituationsAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth } from '../_common';
import {
  createFinancialSituation,
  createFinancialSituationFail,
  createFinancialSituationSuccess,
  deleteFinancialSituationFail,
  deleteFinancialSituationSuccess,
  fetchFinancialSituationsFail,
  fetchFinancialSituationsSuccess,
  updateFinancialSituation,
  updateFinancialSituationFail,
  updateFinancialSituationSuccess,
  upsertFinancialSituationsFail,
  upsertFinancialSituationsSuccess,
} from './actions';
import FinancialSituationAPI from './api';
import { getFinancialSituationId } from './utils';

export function* createFinancialSituationWorker(action: CreateFinancialSituationAction): any {
  try {
    yield waitForAuth();
    const {
      applicationId,
      financialSituation: { applicantId, clientId },
    } = action;

    const financialSituation = omit(action.financialSituation, [
      'clientId',
      'applicationId',
      'applicantId',
    ]) as ServerFinancialSituationCreate;

    const response = yield call(FinancialSituationAPI.create, applicationId, applicantId, financialSituation);

    yield put(createFinancialSituationSuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      financialSituation: { clientId },
    } = action;
    yield put(createFinancialSituationFail(clientId, e.response?.data || e.message));
  }
}

export function* deleteFinancialSituationWorker(action: DeleteFinancialSituationAction) {
  try {
    yield waitForAuth();
    const { applicationId, applicantId, clientId } = action;

    yield call(FinancialSituationAPI.del, applicationId, applicantId, clientId);

    yield put(deleteFinancialSituationSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield put(deleteFinancialSituationFail(clientId, e.response?.data || e.message));
  }
}

export function* fetchFinancialSituationsWorker(action: FetchFinancialSituationsAction): any {
  try {
    yield waitForAuth();

    const { applicationId, applicantId } = action;

    let response;
    if (applicantId) {
      response = yield call(FinancialSituationAPI.fetch, applicationId, applicantId);
    } else {
      response = yield call(FinancialSituationAPI.fetchAll, applicationId);
    }

    const financialSituations = response.data.map((financialSituation: any) =>
      omit(financialSituation, ['meta', 'updatedAt'])
    );
    yield put(fetchFinancialSituationsSuccess(financialSituations));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(fetchFinancialSituationsFail(e.message));
  }
}

export function* updateFinancialSituationWorker(action: UpdateFinancialSituationAction): any {
  try {
    yield waitForAuth();
    const {
      applicationId,
      financialSituation: { applicantId },
    } = action;
    const financialSituationId = yield getFinancialSituationId(
      action.financialSituation.clientId,
      action.financialSituation.id
    );

    const financialSituation = omit(action.financialSituation, [
      'id',
      'clientId',
      'createdAt',
      'isDeidentified',
      'isDeleted',
      'applicationId',
      'applicantId',
    ]) as ServerFinancialSituationUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!financialSituationId) {
      yield all([put(createFinancialSituation(applicationId, action.financialSituation))]);
    } else {
      yield call(FinancialSituationAPI.update, applicationId, applicantId, financialSituation, financialSituationId);

      yield put(updateFinancialSituationSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      financialSituation: { clientId },
    } = action;
    yield put(updateFinancialSituationFail(clientId, e.response?.data || e.message));
  }
}

export function* upsertFinancialSituationsWorker(action: UpsertFinancialSituationsAction) {
  try {
    yield waitForAuth();

    const { applicationId, financialSituations } = action;

    const { financialSituations: reduxStoreFinancialSituations }: { financialSituations: FinancialSituation[] } =
      yield select((state: ReduxStoreData) => ({
        financialSituations: state.financialSituations.data,
      }));

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedFinancialSituations = differenceWith(
      financialSituations,
      reduxStoreFinancialSituations,
      ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) => isEqual(rest1, rest2)
    );

    yield all(
      cloneDeep(changedFinancialSituations).map((clientFinancialSituation) => {
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreFinancialSituation = find(reduxStoreFinancialSituations, {
          clientId: clientFinancialSituation.clientId,
        }) as FinancialSituation;

        if (!reduxStoreFinancialSituation) {
          // No financialSituation
          return put(createFinancialSituation(applicationId, clientFinancialSituation));
        } else if (!reduxStoreFinancialSituation.id) {
          // financialSituation but no id, therefore create has been fired but success hasn't returned
          return put(updateFinancialSituation(applicationId, clientFinancialSituation));
        }

        // FinancialSituation is in the store and has an id
        return put(
          updateFinancialSituation(applicationId, { id: reduxStoreFinancialSituation.id, ...clientFinancialSituation })
        );
      })
    );
    yield put(upsertFinancialSituationsSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(upsertFinancialSituationsFail(e.message));
  }
}

export function* financialSituationsWatcher() {
  yield takeEvery(
    REDUX_STORE.FINANCIAL_SITUATIONS.ACTION_TYPES.CREATE_FINANCIAL_SITUATION,
    createFinancialSituationWorker
  );
  yield takeEvery(
    REDUX_STORE.FINANCIAL_SITUATIONS.ACTION_TYPES.DELETE_FINANCIAL_SITUATION,
    deleteFinancialSituationWorker
  );
  yield takeEvery(
    REDUX_STORE.FINANCIAL_SITUATIONS.ACTION_TYPES.FETCH_FINANCIAL_SITUATIONS,
    fetchFinancialSituationsWorker
  );
  yield takeEvery(
    REDUX_STORE.FINANCIAL_SITUATIONS.ACTION_TYPES.UPDATE_FINANCIAL_SITUATION,
    updateFinancialSituationWorker
  );
  yield takeEvery(
    REDUX_STORE.FINANCIAL_SITUATIONS.ACTION_TYPES.UPSERT_FINANCIAL_SITUATIONS,
    upsertFinancialSituationsWorker
  );
}

export default financialSituationsWatcher;
